import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import clsx from 'clsx'
import { adminService } from '../services/admin'
import { sectorPath, useSector } from '../sector/sector'
import { apiErrorMessage } from '@lipaflow/shared'

const stepLabels: Record<string, string> = {
  school_details: 'Institution Details',
  verification: 'Verification',
  subscription: 'Subscription',
  whatsapp: 'WhatsApp Setup',
  admin_user: 'Admin User',
  data_import: 'Data Import',
  go_live: 'Go Live',
  admin_create_institution: 'Create Customer',
  admin_verify_institution: 'Verification',
  admin_setup_subscription: 'Trial',
  admin_setup_whatsapp: 'WhatsApp Setup',
  admin_create_user: 'Admin User',
  admin_import_data: 'Data Import',
  admin_complete: 'Complete',
}

type OnboardingTab = 'pending' | 'stalled' | 'completed'

function parseTab(value: string | null): OnboardingTab {
  if (value === 'stalled' || value === 'completed') return value
  return 'pending'
}

/** A sector's customers being set up. */
export default function OnboardingPage() {
  const navigate = useNavigate()
  const sector = useSector()
  const code = sector.code
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<OnboardingTab>(parseTab(searchParams.get('tab')))
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null)
  const [selectedTenantName, setSelectedTenantName] = useState<string | null>(null)
  const [staffUserId, setStaffUserId] = useState('')
  const staff = useQuery({ queryKey: ['admin-staff'], queryFn: () => adminService.listStaff() })

  useEffect(() => {
    setActiveTab(parseTab(searchParams.get('tab')))
  }, [searchParams])

  const switchTab = (tab: OnboardingTab) => {
    setActiveTab(tab)
    const next = new URLSearchParams(searchParams)
    next.set('tab', tab)
    setSearchParams(next, { replace: true })
  }

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['onboarding', code, 'stats'],
    queryFn: () => adminService.getOnboardingStats(code),
  })

  const { data: pending, isLoading: pendingLoading } = useQuery({
    queryKey: ['onboarding', code, 'pending'],
    queryFn: () => adminService.getPendingOnboardings(code),
  })

  const { data: stalled, isLoading: stalledLoading } = useQuery({
    queryKey: ['onboarding', code, 'stalled'],
    queryFn: () => adminService.getStalledOnboardings(code),
  })

  const { data: completed, isLoading: completedLoading } = useQuery({
    queryKey: ['onboarding', code, 'completed'],
    queryFn: () => adminService.getCompletedOnboardings(code),
  })

  const assignMutation = useMutation({
    mutationFn: ({ tenantId, staffId }: { tenantId: string; staffId: string }) =>
      adminService.assignOnboarding(tenantId, staffId),
    onSuccess: () => {
      toast.success('Onboarding assigned successfully')
      queryClient.invalidateQueries({ queryKey: ['onboarding'] })
      setShowAssignModal(false)
      setStaffUserId('')
      setSelectedTenantId(null)
      setSelectedTenantName(null)
    },
    onError: (error: unknown) => {
      toast.error(apiErrorMessage(error, 'Failed to assign onboarding'))
    },
  })

  const tabs = [
    { id: 'pending' as const, label: `Pending (${pending?.length ?? 0})` },
    { id: 'stalled' as const, label: `Stalled (${stalled?.length ?? 0})` },
    { id: 'completed' as const, label: `Completed (${completed?.length ?? 0})` },
  ]

  const openAssignModal = (tenantId: string, tenantName: string | null) => {
    setSelectedTenantId(tenantId)
    setSelectedTenantName(tenantName || tenantId)
    setShowAssignModal(true)
  }

  const cardClass = (tab: OnboardingTab) =>
    clsx(
      'bg-surface rounded-2xl shadow-card border p-5 text-left transition-all',
      activeTab === tab ? 'border-accent ring-2 ring-accent' : 'border-hairline hover:border-accent'
    )

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-outfit text-h1 text-ink">Onboarding Pipeline</h1>
          <p className="font-inter text-body text-ink-muted mt-1">
            {sector.name} customers being set up
          </p>
        </div>
        <button
          onClick={() => navigate(sectorPath(code, 'onboarding/new'))}
          className="btn-primary"
        >
          <ClipboardDocumentListIcon className="h-5 w-5" />
          Start New Onboarding
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {statsLoading ? (
          [1, 2, 3, 4, 5].map(i => (
            <div key={i} className="bg-surface rounded-2xl shadow-card p-5 border border-hairline animate-pulse">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-card bg-surface-alt" />
                <div className="space-y-2">
                  <div className="h-3 bg-surface-alt rounded w-20" />
                  <div className="h-6 bg-surface-alt rounded w-10" />
                </div>
              </div>
            </div>
          ))
        ) : (
          <>
            <button type="button" onClick={() => switchTab('pending')} className={cardClass('pending')}>
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-card bg-accent-soft">
                  <ClipboardDocumentListIcon className="h-6 w-6 text-accent" />
                </div>
                <div>
                  <p className="font-inter text-body-sm text-ink-muted">Total</p>
                  <p className="font-outfit text-h3 text-ink">{stats?.total ?? 0}</p>
                </div>
              </div>
            </button>
            <button type="button" onClick={() => switchTab('pending')} className={cardClass('pending')}>
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-card bg-warning-soft">
                  <ClockIcon className="h-6 w-6 text-warning" />
                </div>
                <div>
                  <p className="font-inter text-body-sm text-ink-muted">In Progress</p>
                  <p className="font-outfit text-h3 text-ink">{stats?.in_progress ?? 0}</p>
                </div>
              </div>
            </button>
            <button type="button" onClick={() => switchTab('pending')} className={cardClass('pending')}>
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-card bg-info-50">
                  <ClipboardDocumentListIcon className="h-6 w-6 text-info-600" />
                </div>
                <div>
                  <p className="font-inter text-body-sm text-ink-muted">Pending</p>
                  <p className="font-outfit text-h3 text-ink">{pending?.length ?? 0}</p>
                </div>
              </div>
            </button>
            <button type="button" onClick={() => switchTab('completed')} className={cardClass('completed')}>
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-card bg-success-soft">
                  <CheckCircleIcon className="h-6 w-6 text-success" />
                </div>
                <div>
                  <p className="font-inter text-body-sm text-ink-muted">Completed</p>
                  <p className="font-outfit text-h3 text-ink">{stats?.completed ?? 0}</p>
                </div>
              </div>
            </button>
            <button type="button" onClick={() => switchTab('stalled')} className={cardClass('stalled')}>
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-card bg-clay-50">
                  <ExclamationTriangleIcon className="h-6 w-6 text-clay-600" />
                </div>
                <div>
                  <p className="font-inter text-body-sm text-ink-muted">Stalled</p>
                  <p className="font-outfit text-h3 text-clay-600">{stalled?.length ?? 0}</p>
                </div>
              </div>
            </button>
          </>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-hairline">
        <div className="flex gap-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => switchTab(tab.id)}
              className={clsx(
                'pb-3 px-1 font-inter text-body font-medium border-b-2 transition-colors',
                activeTab === tab.id
                  ? 'border-accent text-accent'
                  : 'border-transparent text-ink-muted hover:text-ink'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Pending Tab */}
      {activeTab === 'pending' && (
        <div className="bg-surface rounded-2xl shadow-card border border-hairline overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-alt border-b border-hairline">
                  <th className="px-6 py-4 text-left font-outfit text-caption text-ink-muted uppercase tracking-wider">Customer</th>
                  <th className="px-6 py-4 text-left font-outfit text-caption text-ink-muted uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left font-outfit text-caption text-ink-muted uppercase tracking-wider">Current Step</th>
                  <th className="px-6 py-4 text-left font-outfit text-caption text-ink-muted uppercase tracking-wider">Assigned To</th>
                  <th className="px-6 py-4 text-left font-outfit text-caption text-ink-muted uppercase tracking-wider">Started</th>
                  <th className="px-6 py-4 text-right font-outfit text-caption text-ink-muted uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline">
                {pendingLoading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                        <p className="font-inter text-body-sm text-ink-muted">Loading...</p>
                      </div>
                    </td>
                  </tr>
                ) : !pending?.length ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <CheckCircleIcon className="h-12 w-12 text-ink-faint" />
                        <div>
                          <p className="font-outfit text-h4 text-ink">All caught up</p>
                          <p className="font-inter text-body-sm text-ink-muted mt-1">
                            No pending onboardings at the moment
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  pending.map(item => (
                    <tr key={item.tenant_id} className="hover:bg-accent-soft/30 transition-colors">
                      <td className="px-6 py-4">
                        <p className="font-inter text-body font-medium text-ink">
                          {item.tenant_name || item.tenant_id}
                        </p>
                        {item.is_admin_setup && (
                          <span className="inline-flex px-2 py-0.5 rounded text-caption font-medium bg-accent-soft text-accent mt-1">
                            Admin Setup
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex px-2.5 py-1 rounded-full text-caption font-medium bg-warning-soft text-warning">
                          {item.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-inter text-body-sm text-ink">
                          {stepLabels[item.current_step] || item.current_step}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        {item.assigned_to ? (
                          <span className="font-inter text-body-sm text-ink">{item.assigned_to}</span>
                        ) : (
                          <span className="font-inter text-body-sm text-ink-faint italic">Unassigned</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-inter text-body-sm text-ink-muted">
                          {item.started_at
                            ? new Date(item.started_at).toLocaleDateString('en-KE', { month: 'short', day: 'numeric', year: 'numeric' })
                            : '-'}
                        </p>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => navigate(sectorPath(code, `onboarding/${item.tenant_id}`))}
                            className="font-inter text-body-sm text-accent hover:text-accent-hover font-medium px-3 py-1.5 rounded-button hover:bg-accent-soft transition-colors"
                          >
                            Continue
                          </button>
                          <button
                            onClick={() => openAssignModal(item.tenant_id, item.tenant_name)}
                            className="font-inter text-body-sm text-ink-muted hover:text-ink font-medium px-3 py-1.5 rounded-button hover:bg-surface-alt transition-colors"
                          >
                            <UserPlusIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Stalled Tab */}
      {activeTab === 'stalled' && (
        <div className="bg-surface rounded-2xl shadow-card border border-hairline overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-alt border-b border-hairline">
                  <th className="px-6 py-4 text-left font-outfit text-caption text-ink-muted uppercase tracking-wider">Customer</th>
                  <th className="px-6 py-4 text-left font-outfit text-caption text-ink-muted uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left font-outfit text-caption text-ink-muted uppercase tracking-wider">Current Step</th>
                  <th className="px-6 py-4 text-left font-outfit text-caption text-ink-muted uppercase tracking-wider">Reminders Sent</th>
                  <th className="px-6 py-4 text-left font-outfit text-caption text-ink-muted uppercase tracking-wider">Last Reminder</th>
                  <th className="px-6 py-4 text-right font-outfit text-caption text-ink-muted uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline">
                {stalledLoading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                        <p className="font-inter text-body-sm text-ink-muted">Loading...</p>
                      </div>
                    </td>
                  </tr>
                ) : !stalled?.length ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <CheckCircleIcon className="h-12 w-12 text-ink-faint" />
                        <div>
                          <p className="font-outfit text-h4 text-ink">No stalled onboardings</p>
                          <p className="font-inter text-body-sm text-ink-muted mt-1">
                            All customers are progressing through onboarding
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  stalled.map(item => (
                    <tr key={item.tenant_id} className="hover:bg-accent-soft/30 transition-colors">
                      <td className="px-6 py-4">
                        <p className="font-inter text-body font-medium text-ink">
                          {item.tenant_name || item.tenant_id}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex px-2.5 py-1 rounded-full text-caption font-medium bg-clay-100 text-clay-700">
                          {item.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-inter text-body-sm text-ink">
                          {stepLabels[item.current_step] || item.current_step}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-inter text-body-sm text-ink">{item.reminders_sent}</span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-inter text-body-sm text-ink-muted">
                          {item.last_reminder_sent_at
                            ? new Date(item.last_reminder_sent_at).toLocaleDateString('en-KE', { month: 'short', day: 'numeric', year: 'numeric' })
                            : 'Never'}
                        </p>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => navigate(sectorPath(code, `onboarding/${item.tenant_id}`))}
                            className="font-inter text-body-sm text-accent hover:text-accent-hover font-medium px-3 py-1.5 rounded-button hover:bg-accent-soft transition-colors"
                          >
                            Continue
                          </button>
                          <button
                            onClick={() => openAssignModal(item.tenant_id, item.tenant_name)}
                            className="font-inter text-body-sm text-ink-muted hover:text-ink font-medium px-3 py-1.5 rounded-button hover:bg-surface-alt transition-colors"
                          >
                            <UserPlusIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Completed Tab */}
      {activeTab === 'completed' && (
        <div className="bg-surface rounded-2xl shadow-card border border-hairline overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-alt border-b border-hairline">
                  <th className="px-6 py-4 text-left font-outfit text-caption text-ink-muted uppercase tracking-wider">Customer</th>
                  <th className="px-6 py-4 text-left font-outfit text-caption text-ink-muted uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left font-outfit text-caption text-ink-muted uppercase tracking-wider">Last Step</th>
                  <th className="px-6 py-4 text-left font-outfit text-caption text-ink-muted uppercase tracking-wider">Started</th>
                  <th className="px-6 py-4 text-left font-outfit text-caption text-ink-muted uppercase tracking-wider">Completed</th>
                  <th className="px-6 py-4 text-right font-outfit text-caption text-ink-muted uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline">
                {completedLoading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                        <p className="font-inter text-body-sm text-ink-muted">Loading...</p>
                      </div>
                    </td>
                  </tr>
                ) : !completed?.length ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <CheckCircleIcon className="h-12 w-12 text-ink-faint" />
                        <div>
                          <p className="font-outfit text-h4 text-ink">No completed onboardings</p>
                          <p className="font-inter text-body-sm text-ink-muted mt-1">
                            Complete customer onboarding to see entries here
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  completed.map(item => (
                    <tr key={item.tenant_id} className="hover:bg-accent-soft/30 transition-colors">
                      <td className="px-6 py-4">
                        <p className="font-inter text-body font-medium text-ink">
                          {item.tenant_name || item.tenant_id}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex px-2.5 py-1 rounded-full text-caption font-medium bg-success-soft text-success">
                          {item.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-inter text-body-sm text-ink">
                          {stepLabels[item.current_step || ''] || item.current_step || 'go_live'}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-inter text-body-sm text-ink-muted">
                          {item.started_at
                            ? new Date(item.started_at).toLocaleDateString('en-KE', { month: 'short', day: 'numeric', year: 'numeric' })
                            : '-'}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-inter text-body-sm text-ink-muted">
                          {item.completed_at
                            ? new Date(item.completed_at).toLocaleDateString('en-KE', { month: 'short', day: 'numeric', year: 'numeric' })
                            : '-'}
                        </p>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => navigate(sectorPath(code, `onboarding/${item.tenant_id}`))}
                          className="font-inter text-body-sm text-accent hover:text-accent-hover font-medium px-3 py-1.5 rounded-button hover:bg-accent-soft transition-colors"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-scrim/50 backdrop-blur-sm transition-opacity"
              onClick={() => {
                setShowAssignModal(false)
                setStaffUserId('')
                setSelectedTenantId(null)
                setSelectedTenantName(null)
              }}
            />
            <div className="relative bg-surface rounded-2xl shadow-xl w-full max-w-md animate-scale-in">
              <div className="flex items-center justify-between px-6 py-4 border-b border-hairline">
                <div>
                  <h3 className="font-outfit text-h3 text-ink">Assign Onboarding</h3>
                  <p className="font-inter text-body-sm text-ink-muted mt-0.5">
                    Assign a staff member to manage this onboarding
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowAssignModal(false)
                    setStaffUserId('')
                    setSelectedTenantId(null)
                    setSelectedTenantName(null)
                  }}
                  className="p-2 text-ink-faint hover:text-ink-muted hover:bg-surface-alt rounded-button transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="rounded-card border border-accent bg-accent-soft p-3">
                  <p className="font-inter text-caption text-accent">Target customer</p>
                  <p className="font-inter text-body-sm font-medium text-accent mt-1">{selectedTenantName || '-'}</p>
                  {selectedTenantId && (
                    <p className="font-mono text-caption text-accent mt-1">{selectedTenantId}</p>
                  )}
                </div>
                <label className="label" htmlFor="onboarding-staff">Who should help them set up?</label>
                {/* A picker of LipaFlow staff; this used to ask for a pasted user ID. */}
                <select
                  id="onboarding-staff"
                  value={staffUserId}
                  onChange={(e) => setStaffUserId(e.target.value)}
                  className="input"
                >
                  <option value="">{staff.isLoading ? 'Loading…' : 'Choose…'}</option>
                  {(staff.data ?? []).map((m) => (
                    <option key={m.id} value={m.id}>{m.full_name} · {m.email}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-hairline bg-surface-alt rounded-b-2xl">
                <button
                  onClick={() => {
                    setShowAssignModal(false)
                    setStaffUserId('')
                    setSelectedTenantId(null)
                    setSelectedTenantName(null)
                  }}
                  className="btn-ghost"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (selectedTenantId && staffUserId) {
                      assignMutation.mutate({ tenantId: selectedTenantId, staffId: staffUserId })
                    }
                  }}
                  disabled={!staffUserId || assignMutation.isPending}
                  className="btn-primary"
                >
                  {assignMutation.isPending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Assigning...
                    </>
                  ) : (
                    <>
                      <UserPlusIcon className="h-5 w-5" />
                      Assign
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
